import { ethers } from 'ethers';
import { encodeFunctionData } from 'viem';

import { abiAquarium, abiFishFactory, abiFishNFT, abiFishingGame } from '../config/abi.contract';
import useWrappedRPC from './useWrappedRPC';
import {
  generateTxnSignature,
  validateCollectFishTransaction,
  validateDepositFishTransaction,
  validateMintFishTransaction,
  validateWithdrawFishTransaction,
} from '../services/transaction.service';
import environments from '../utils/environments';
import useUserStore from '../stores/user.store';

const {
  FISH_NFT_CONTRACT_ADDRESS,
  FISH_FACTORY_CONTRACT_ADDRESS,
  FISHING_GAME_CONTRACT_ADDRESS,
  AQUARIUM_CONTRACT_ADDRESS,
} = environments;

function useFish() {
  const { sendTransactionWithGas, provider } = useWrappedRPC();
  const user = useUserStore((state) => state.user);

  const withdrawFish = async (fishIds, tokenIds) => {
    try {
      const signatureRes = await generateTxnSignature({
        nameFunction: 'signatureWithdrawFish',
        params: {
          fishIds: fishIds,
          tokenIds: tokenIds,
          quantity: fishIds.length,
        },
      });
      const signatureData = signatureRes.data;

      const data = encodeFunctionData({
        abi: abiFishingGame,
        functionName: 'withdrawOrMintBatchFishWithSignature',
        args: [signatureData.req, signatureData.signature],
      });

      const txUiConfig = {
        header: `Withdraw Fish`,
        description: `Withdraw fish`,
        buttonText: `Withdraw`,
      };

      // Construct transaction request
      const transactionRequest = {
        to: FISHING_GAME_CONTRACT_ADDRESS,
        data: data,
      };

      const tx = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!tx) throw new Error('Transaction failed on blockchain');

      const backendResponse = await validateWithdrawFishTransaction({ transactionHash: tx.hash });

      return {
        success: true,
        message: 'Verify successful',
        data: backendResponse.data,
      };
    } catch (error) {
      console.error('Withdraw fish transaction failed:', error);
      return { success: false, message: error.message };
    }
  };

  const mintFish = async (fishIds) => {
    try {
      const signatureRes = await generateTxnSignature({
        nameFunction: 'signatureMintFish',
        params: {
          fishIds: fishIds,
          quantity: fishIds.length,
        },
      });
      const signatureData = signatureRes.data;

      const data = encodeFunctionData({
        abi: abiFishFactory,
        functionName: 'MintBatchFishWithSignature',
        args: [signatureData.req, signatureData.signature],
      });

      const txUiConfig = {
        header: `Mint Fish`,
        description: `Mint fish`,
        buttonText: `Mint`,
      };

      // Construct transaction request
      const transactionRequest = {
        to: FISH_FACTORY_CONTRACT_ADDRESS,
        data: data,
      };

      const tx = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!tx) throw new Error('Transaction failed on blockchain');

      const backendResponse = await validateMintFishTransaction({ transactionHash: tx.hash });

      return {
        success: true,
        message: 'Verify successful',
        data: backendResponse.data,
      };
    } catch (error) {
      console.error('mint fish transaction failed:', error);
      return { success: false, message: error.message };
    }
  };

  const depositFish = async (tokenIds) => {
    try {
      const fishNftContract = new ethers.Contract(FISH_NFT_CONTRACT_ADDRESS, abiFishNFT, provider);

      const isApproved = await fishNftContract.isApprovedForAll(user.walletAddress, FISHING_GAME_CONTRACT_ADDRESS);

      if (!isApproved) {
        const approvalData = encodeFunctionData({
          abi: abiFishNFT,
          functionName: 'setApprovalForAll',
          args: [FISHING_GAME_CONTRACT_ADDRESS, true],
        });

        await sendTransactionWithGas(
          {
            to: FISH_NFT_CONTRACT_ADDRESS,
            data: approvalData,
          },
          {
            header: 'Approve all Fish',
            description: `Approve all fish for Fishing Game`,
            buttonText: 'Approve all',
          }
        );
      }

      const data = encodeFunctionData({
        abi: abiFishingGame,
        functionName: 'stakeFish',
        args: [tokenIds],
      });

      const tokenIdsList = tokenIds.join(', ');
      const txUiConfig = {
        header: `Deposit Fish`,
        description: `Deposit Fish IDs: ${tokenIdsList}`,
        buttonText: `Deposit`,
      };

      // Construct transaction request
      const transactionRequest = {
        to: FISHING_GAME_CONTRACT_ADDRESS,
        data: data,
      };

      const txReceipt = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!txReceipt) throw new Error('Transaction failed on blockchain');

      const backendResponse = await validateDepositFishTransaction({ transactionHash: txReceipt.hash });

      return {
        success: true,
        message: 'Verify successful',
        data: backendResponse.data,
      };
    } catch (error) {
      console.error('Deposit fish transaction failed:', error);
      return { success: false, message: error.message };
    }
  };

  const cooking = async (stakedTokenIds, tokenIds) => {
    try {
      const data = encodeFunctionData({
        abi: abiFishingGame,
        functionName: 'cooking',
        args: [stakedTokenIds, tokenIds],
      });

      const listStakedTokenIds = stakedTokenIds.join(', ');
      const listtokenIds = tokenIds.join(', ');
      const txUiConfig = {
        header: `Cooking Fish`,
        description: `Cooking Fish IDs: ${listStakedTokenIds}, ${listtokenIds}`,
        buttonText: `Cooking`,
      };

      // Construct transaction request
      const transactionRequest = {
        to: FISHING_GAME_CONTRACT_ADDRESS,
        data: data,
      };

      const txReceipt = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!txReceipt) throw new Error('Transaction failed on blockchain');

      return {
        success: true,
        message: 'Verify successful',
        transactionHash: txReceipt.hash,
      };
    } catch (error) {
      console.error('Cook transaction failed:', error);
      return { success: false, message: error.message };
    }
  };

  const addToCollection = async (fish) => {
    try {
      const tokenIds = fish.reduce((arr, fish) => [...arr, ...fish.tokenIds], []);
      const data = encodeFunctionData({
        abi: abiAquarium,
        functionName: 'addCollection',
        args: [tokenIds],
      });

      const listtokenIds = tokenIds.join(', ');
      const txUiConfig = {
        header: `Add to collection`,
        description: `Collecting Fish IDs: ${listtokenIds}`,
        buttonText: `Collect`,
      };

      // Construct transaction request
      const transactionRequest = {
        to: AQUARIUM_CONTRACT_ADDRESS,
        data: data,
      };

      const txReceipt = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!txReceipt) throw new Error('Transaction failed on blockchain');

      const backendResponse = await validateCollectFishTransaction({ fish, transactionHash: txReceipt.hash });
      const fishIds = fish.map((f) => f.fishId);

      return {
        success: true,
        message: 'Verify successful',
        data: { ...backendResponse.data, fishIds },
      };
    } catch (error) {
      console.error('add to collection failed:', error);
      return { success: false, message: error.message };
    }
  };

  return { withdrawFish, mintFish, depositFish, cooking, addToCollection };
}

export default useFish;
